import GuideLink from 'components/GuideLink'
import ConnectWalletButton from 'components/ConnectWalletButton'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import useSWR, { mutate as mutateGlobal } from 'swr'
import { useProfile } from 'state/profile/hooks'
import { ProfileRewardCard } from 'views/Profile/components/ProfileRewards'
import { UserRewardCard } from 'views/TradingReward/components/UserRewardCard'
import { AlertsCard } from 'pwa/AlertsToggle'
import { openPwaSheet } from 'pwa/events'
import { ApiError, Avatar, Profile, api, errorText } from './api'
import { handleHint } from './handleRules'
import TeamInvites from './TeamInvites'
import { AvatarImg, GLOW } from './avatar'
import { PnlCard, usePnlEnabled } from './PnlCard'
import { COVERS, Cover } from './covers'
import { ActionBtn, Drawer, Hint, Icon, IdentityCard, MY_TABS, Note, Surface, TabPanel, Tabs, useShare, useTab } from './Identity'
import { ActivityCard, ProgressCard, UnclaimedProfile } from './AutoProfile'
import { NftsCard } from './NftsCard'
import BuyNftSheet from './BuyNftSheet'
import OnchainEditor from './OnchainEditor'
import { OnchainProfileCard, ReferralCard } from './OnchainCard'
import { inviterLabel } from './referral'
import { useInviter } from './useReferral'
import { People } from './PublicProfile'
import { useSocialSession } from './useSocialSession'
import { Achievements, Btn, Muted, Tag } from './ui'
import EarnSection from './earn/EarnSection'

const Page = styled.main`
  width: 100%;
  max-width: 960px;
  margin: 0 auto;
  padding: 16px 16px 120px;
  display: grid;
  gap: 14px;
  ${({ theme }) => theme.mediaQueries.md} {
    padding: 28px 24px 80px;
    gap: 16px;
  }
`
const Input = styled.input`
  flex: 1;
  min-width: 0;
  min-height: 48px;
  padding: 0 14px;
  border-radius: 14px;
  font-size: 17px;
  color: ${({ theme }) => theme.colors.text};
  background: rgba(0, 0, 0, 0.25);
  border: 1px solid rgba(255, 255, 255, 0.14);
  &:focus-visible {
    outline: 2px solid ${GLOW};
    border-color: transparent;
  }
`
const Picker = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  button {
    border: 2px solid transparent;
    border-radius: 16px;
    padding: 6px;
    background: rgba(255, 255, 255, 0.04);
    color: inherit;
    cursor: pointer;
    display: grid;
    justify-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 600;
    min-height: 44px;
    text-transform: capitalize;
  }
  button[aria-pressed='true'] {
    border-color: ${GLOW};
    box-shadow: 0 0 18px -4px ${GLOW};
  }
  button:focus-visible {
    outline: 2px solid ${GLOW};
  }
`
const HandleError = styled.p`
  margin: 0;
  padding: 10px 12px;
  border-radius: 12px;
  font-size: 14px;
  line-height: 20px;
  color: #ffd0d6;
  background: rgba(237, 75, 158, 0.1);
  border: 1px solid rgba(237, 75, 158, 0.45);
`
const Field = styled.div`
  display: grid;
  gap: 10px;
  h3 {
    margin: 0;
    font-size: 16px;
    font-weight: 800;
    letter-spacing: -0.005em;
  }
`
const Toggle = styled.label`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 48px;
  font-size: 16px;
  color: ${({ theme }) => theme.colors.text};
  input {
    appearance: none;
    -webkit-appearance: none;
    width: 50px;
    height: 30px;
    margin: 0;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.12);
    border: 1px solid rgba(255, 255, 255, 0.1);
    position: relative;
    cursor: pointer;
    transition: background 180ms;
    flex: none;
  }
  input::after {
    content: '';
    position: absolute;
    top: 3px;
    left: 3px;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.4);
    transition: transform 180ms cubic-bezier(0.2, 0.8, 0.2, 1);
  }
  input:checked {
    background: linear-gradient(135deg, #7fd4e8, #9a7bff);
    border-color: transparent;
  }
  input:checked::after {
    transform: translateX(20px);
  }
  input:focus-visible {
    outline: 2px solid ${GLOW};
    outline-offset: 2px;
  }
  input:disabled {
    opacity: 0.5;
  }
  @media (prefers-reduced-motion: reduce) {
    input,
    input::after {
      transition: none;
    }
  }
`

export default function MyProfile() {
  const { address, session, me: sessionMe, setMe, signIn, signOut, busy, error } = useSocialSession()
  const inviter = useInviter(address)
  const { profile: onchain } = useProfile()
  // 2026-10-02 (Bez): a connected wallet with a CADINU profile opens it directly. The page reads the public profile; a signature is
  // asked only when something is saved ("Confirm it's you to save") and the session it opens is kept until it expires.
  const { data: publicMe, error: publicError } = useSWR<Profile>(address && !session ? `my-profile:${address.toLowerCase()}` : null, () => api<Profile>(`profiles/${address}`), {
    revalidateOnFocus: false,
    shouldRetryOnError: false,
  })
  const noProfile = publicMe?.state === 'none' || (publicError instanceof ApiError && publicError.status === 404)
  const me: Profile | null = session ? sessionMe : publicMe && publicMe.state !== 'none' ? { ...publicMe, is_me: true } : null
  const [handle, setHandle] = useState('')
  const [handleErr, setHandleErr] = useState<string | null>(null)
  const [msg, setMsg] = useState<string | null>(null)
  const [nfts, setNfts] = useState<Avatar[] | null>(null)
  const [working, setWorking] = useState(false)
  const [edit, setEdit] = useState(false)
  const [focusChain, setFocusChain] = useState(false)
  const [list, setList] = useState<'followers' | 'following' | null>(null)
  const [nftCount, setNftCount] = useState<number | undefined>()
  const [tab, setTab] = useTab('achievements', MY_TABS)
  // arriving at /u#earn (the competitions' "Rewards & referral" tab links there) scrolls to the Earn section once it is drawn
  const [arrivedAtEarn] = useState(() => typeof window !== 'undefined' && window.location.hash === '#earn')
  const token = session?.token
  const pnlOn = usePnlEnabled()
  const publicUrl = typeof window !== 'undefined' && me ? `${window.location.origin}/u/${me.handle ?? me.address}` : ''
  const { share, done: shared } = useShare(publicUrl, me?.handle ? `@${me.handle} on CADINU` : 'My CADINU profile')
  const closeEdit = useCallback(() => {
    setEdit(false)
    setFocusChain(false)
  }, [])
  // the old /buy-nft and /create-profile land here (next.config redirects): ?buy=1 opens the purchase, ?onchain=1 the on-chain profile
  const router = useRouter()
  const [buyOpen, setBuyOpen] = useState(false)
  const [chainOpen, setChainOpen] = useState(false)
  const closeBuy = useCallback(() => setBuyOpen(false), [])
  const closeChain = useCallback(() => setChainOpen(false), [])
  const openBuy = useCallback(() => {
    setEdit(false)
    setChainOpen(false)
    setBuyOpen(true)
  }, [])
  const hasMe = Boolean(me)
  const ready = Boolean(address) && (hasMe || noProfile)
  useEffect(() => {
    if (!router.isReady || !ready) return
    const { buy, onchain: chainFlag, ...rest } = router.query
    if (buy !== '1' && chainFlag !== '1') return
    if (buy === '1') setBuyOpen(true)
    else if (hasMe) {
      setFocusChain(true)
      setEdit(true)
    } else setChainOpen(true)
    // drop the flag (the menu can link here again; a reload does not reopen it); ?ref= and the tab stay
    router.replace({ pathname: router.pathname, query: rest, hash: window.location.hash.replace('#', '') || undefined }, undefined, { shallow: true })
  }, [router, ready, hasMe])
  useEffect(() => {
    if (!edit || !focusChain) return undefined
    const t = setTimeout(() => document.getElementById('onchain-section')?.scrollIntoView({ block: 'start' }), 120)
    return () => clearTimeout(t)
  }, [edit, focusChain])
  const onBought = useCallback(() => {
    if (address) mutateGlobal(['cadinu-held-nfts', address.toLowerCase()])
  }, [address])
  const buyDrawer = (
    <Drawer open={buyOpen} onClose={closeBuy} title="Buy a CADINU NFT" testId="buy-drawer">
      <BuyNftSheet account={address as `0x${string}` | undefined} refQuery={router.query.ref} onBought={onBought} />
    </Drawer>
  )

  /** the session token, asking the wallet to confirm only now (at a save) when there is none or it has expired */
  const ensureToken = async () => token ?? (await signIn())?.token ?? null
  const call = async (path: string, method: string, body?: unknown, onError?: (text: string) => void) => {
    const tk = await ensureToken()
    if (!tk) return false
    setWorking(true)
    setMsg(null)
    try {
      setMe(await api<Profile>(path, { method, body, token: tk }))
      return true
    } catch (e) {
      if (onError) onError(errorText(e))
      else setMsg(errorText(e))
      return false
    } finally {
      setWorking(false)
    }
  }
  const loadNfts = async () => {
    const tk = await ensureToken()
    if (!tk) return
    setWorking(true)
    setMsg(null)
    try {
      setNfts((await api<{ nfts: Avatar[] }>('me/nfts', { token: tk })).nfts)
    } catch (e) {
      setMsg(errorText(e))
    } finally {
      setWorking(false)
    }
  }
  const setAvatar = (n: Avatar) => call('me/avatar', 'PUT', { collection: n.collection, token_id: n.token_id })

  // 2026-10-07 (Bez): a connected wallet without a CADINU profile gets its profile page at once, built from its public activity, with the
  // progressive steps; claiming it is the one free signature (no transaction). Nothing is created until that signature.
  if (address && !me && noProfile)
    return (
      <Page>
        <UnclaimedProfile wallet={address} mine onClaim={signIn} busy={busy} error={error}>
          {inviter.pending && (
            <Muted data-testid="invited-by" data-state="pending" style={{ marginTop: 10 }}>
              {inviterLabel(inviter.pending)}. Claiming your profile binds it to this wallet for good.
            </Muted>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', paddingTop: 12, marginTop: 12, borderTop: '1px solid rgba(255,255,255,0.06)' }} data-testid="gate-onchain">
            <ActionBtn type="button" onClick={openBuy} data-testid="gate-buy">
              <Icon name="image" size={18} />
              Buy a CADINU NFT
            </ActionBtn>
            <ActionBtn type="button" onClick={() => setChainOpen(true)} data-testid="gate-onchain-open">
              <Icon name="lock" size={18} />
              On-chain profile
            </ActionBtn>
            <GuideLink guide="profile" />
          </div>
        </UnclaimedProfile>
        {/* 2026-10-07: the alerts switch; public alerts need no profile, personal ones come with the claim above (no other signature) */}
        <Surface id="alerts" data-testid="profile-alerts">
          <AlertsCard onClaim={signIn} claimBusy={busy} onInstall={() => openPwaSheet('install')} />
        </Surface>
        {/* 2026-10-09: the affiliate programme needs no claimed profile; registering as referrer is on the create page here */}
        <EarnSection wallet={address} scrollOnMount={arrivedAtEarn} />
        {buyDrawer}
        <Drawer open={chainOpen} onClose={closeChain} title="On-chain profile" testId="onchain-drawer">
          <OnchainEditor wallet={address as `0x${string}`} onBuy={openBuy} bare />
        </Drawer>
      </Page>
    )

  if (!address || !me) {
    const ghost: Profile = { state: 'on', address: address ?? '0x0000000000000000000000000000000000000000' }
    const loading = Boolean(address) && !noProfile && !publicError
    return (
      <Page>
        <IdentityCard p={ghost}>
          <div style={{ marginTop: 16, display: 'grid', gap: 10, justifyItems: 'start' }} data-testid="profile-gate">
            {!address ? (
              <>
                <strong style={{ fontSize: 17 }}>
                  {router.query.buy === '1' ? 'Connect a wallet to buy a CADINU NFT' : router.query.onchain === '1' ? 'Connect a wallet to register on-chain' : 'Connect a wallet to open your profile'}
                </strong>
                <ConnectWalletButton />
              </>
            ) : loading ? (
              <Muted aria-busy="true">Opening your profile…</Muted>
            ) : noProfile ? null : (
              <Muted role="alert">Your profile could not be loaded. Try again in a moment.</Muted>
            )}
            {error && <Muted role="alert">{error}</Muted>}
            <GuideLink guide="profile" style={{ marginTop: 4 }} />
          </div>
        </IdentityCard>
        {/* 2026-10-09: without a wallet the programme itself, with a connect button (no link, no claim) */}
        {!address && <EarnSection scrollOnMount={arrivedAtEarn} />}
        {buyDrawer}
        {address && (
          <Drawer open={chainOpen} onClose={closeChain} title="On-chain profile" testId="onchain-drawer">
            <OnchainEditor wallet={address as `0x${string}`} onBuy={openBuy} bare />
          </Drawer>
        )}
      </Page>
    )
  }

  const hasHandle = Boolean(me.handle)
  const hasAvatar = Boolean(me.avatar)
  const s = me.settings
  const avatarKey = me.avatar ? `${me.avatar.collection}-${me.avatar.token_id}` : null
  const earned = me.achievements?.filter((a) => a.earned).length
  return (
    <Page>
      <IdentityCard
        // no /u avatar yet: the hero shows the NFT locked in the on-chain CADINU profile, as the header pill does (display only;
        // the profile still asks to pick an avatar)
        p={{
          ...me,
          is_me: true,
          avatar:
            me.avatar ??
            (onchain?.nft?.image?.thumbnail
              ? { collection: onchain.collectionAddress ?? '', token_id: String(onchain.tokenId ?? ''), level: null, character: null, image: onchain.nft.image.thumbnail }
              : null),
        }}
        onCounts={(w) => setList(list === w ? null : w)}
        openList={list}
        // eslint-disable-next-line react/no-children-prop
        children={<GuideLink guide="profile" style={{ marginTop: 14 }} />}
        actions={
          <>
            <ActionBtn type="button" $primary onClick={() => setEdit(true)} data-testid="edit-btn">
              <Icon name="edit" size={18} />
              Edit
            </ActionBtn>
            <ActionBtn type="button" onClick={share} data-testid="share-btn" aria-label="Share profile">
              <Icon name={shared ? 'check' : 'share'} size={18} />
              {shared ? 'Link copied' : 'Share'}
            </ActionBtn>
          </>
        }
      />
      {list && <People wallet={me.address} which={list} onClose={() => setList(null)} />}
      {/* 2026-10-07: the progressive steps replace the one-line nudge while the handle or the avatar is missing */}
      {(!hasHandle || !hasAvatar) && <ProgressCard p={me} mine onEdit={() => setEdit(true)} />}
      <TeamInvites
        wallet={me.address}
        token={token}
        ensureToken={ensureToken}
        onChange={() => {
          if (token) api<Profile>('me', { token }).then(setMe).catch(() => undefined)
          mutateGlobal(`my-profile:${me.address.toLowerCase()}`)
        }}
      />
      {/* 2026-10-07: the alerts switch; with this browser's profile session the device also gets personal alerts (no new signature) */}
      <Surface id="alerts" data-testid="profile-alerts">
        <AlertsCard session={session} onClaim={signIn} claimBusy={busy} claimed onInstall={() => openPwaSheet('install')} />
      </Surface>
      <Tabs tab={tab} setTab={setTab} counts={{ achievements: earned, nfts: nftCount }} items={MY_TABS} />
      <TabPanel id="achievements" tab={tab}>
        {me.achievements && <Achievements items={me.achievements} />}
      </TabPanel>
      <TabPanel id="nfts" tab={tab}>
        <NftsCard wallet={me.address} mine onCount={setNftCount} onSetAvatar={setAvatar} avatarKey={avatarKey} onBuy={openBuy} />
      </TabPanel>
      <TabPanel id="rewards" tab={tab}>
        <ReferralCard wallet={me.address as `0x${string}`} id={me.referral?.id} signups={me.referral?.signups} invitedBy={session ? me.invited_by ?? null : undefined} />
        <OnchainProfileCard
          wallet={me.address as `0x${string}`}
          onManage={() => {
            setFocusChain(true)
            setEdit(true)
          }}
          onBuy={openBuy}
        />
        {pnlOn && s?.visible && s?.show_pnl && <PnlCard wallet={me.address} />}
        <ProfileRewardCard />
        <UserRewardCard />
      </TabPanel>
      <TabPanel id="earn" tab={tab}>
        <EarnSection
          wallet={me.address}
          refId={me.referral?.id}
          scrollOnMount={arrivedAtEarn}
          // the one "Register as referrer" button stays on the Referrals card (Rewards tab); Earn opens it
          onRegister={() => {
            setTab('rewards')
            setTimeout(() => document.querySelector('[data-testid="ref-register"]')?.scrollIntoView({ block: 'center', behavior: 'smooth' }), 200)
          }}
        />
      </TabPanel>
      <ActivityCard wallet={me.address} mine />
      {msg && !edit && (
        <Surface role="alert">
          <Muted>{msg}</Muted>
        </Surface>
      )}

      <Drawer open={edit} onClose={closeEdit} title="Edit profile">
        <Muted data-testid="session-note">
          {session
            ? `Changes save directly: this browser is confirmed until ${new Date(session.expiry).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}.`
            : 'Your first change asks your wallet to confirm it’s you (one free signature, no transaction). The confirmation is kept on this browser for 10 hours.'}
        </Muted>
        <Field>
          <Hint label="handles" text='3 to 15 characters: letters, digits, "." and "_", starting with a letter. Change once every 7 days.'>
            <h3>Handle</h3>
          </Hint>
          <form
            onSubmit={async (e) => {
              e.preventDefault()
              const local = handleHint(handle)
              setHandleErr(local)
              if (local) return
              if (await call('me', 'PATCH', { handle: handle.trim() }, setHandleErr)) setHandle('')
            }}
            style={{ display: 'flex', gap: 8 }}
          >
            <Input
              aria-label="Handle"
              placeholder={me.handle ?? 'your.handle'}
              value={handle}
              maxLength={15}
              onChange={(e) => {
                setHandle(e.target.value)
                // an authority word is named as soon as it is typed; the shape waits for the save (no nagging while typing)
                const h = handleHint(e.target.value)
                setHandleErr(h && /Handles cannot/.test(h) ? h : null)
              }}
              aria-invalid={Boolean(handleErr)}
              aria-describedby={handleErr ? 'handle-error' : undefined}
              data-testid="handle-input"
            />
            <Btn type="submit" disabled={working || busy || handle.trim().length < 3}>
              {token ? 'Save' : 'Confirm it\'s you to save'}
            </Btn>
          </form>
          {handleErr && (
            <HandleError id="handle-error" role="alert" data-testid="handle-error">
              {handleErr}
            </HandleError>
          )}
        </Field>

        <Field>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <Hint label="avatars" text="Any CADINU Identity Arts NFT your wallet holds, or the one locked in your CADINU profile. Ownership is checked on BNB Chain.">
              <h3>Avatar</h3>
            </Hint>
            {hasAvatar ? <Tag $tone="ok">Verified on BNB Chain</Tag> : <Tag $tone="warn">Not set</Tag>}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <AvatarImg avatar={me.avatar} size={56} />
            <Btn type="button" $ghost onClick={loadNfts} disabled={working}>
              {nfts ? 'Refresh my NFTs' : 'Show my NFTs'}
            </Btn>
            {hasAvatar && (
              <Btn type="button" $ghost onClick={() => call('me/avatar', 'DELETE')} disabled={working}>
                Remove avatar
              </Btn>
            )}
          </div>
          {nfts && !nfts.length && (
            <Note>
              This wallet holds no CADINU NFT.{' '}
              <button type="button" onClick={openBuy} style={{ background: 'none', border: 0, padding: 0, color: 'inherit', textDecoration: 'underline', cursor: 'pointer', font: 'inherit' }}>
                Buy one
              </button>
              .
            </Note>
          )}
          {nfts && nfts.length > 0 && (
            <Picker>
              {nfts.map((n) => {
                const k = `${n.collection}-${n.token_id}`
                return (
                  <button key={k} type="button" aria-pressed={avatarKey === k} disabled={working} onClick={() => setAvatar(n)}>
                    <AvatarImg avatar={n} size={64} />
                    <span>
                      {n.character} #{n.token_id}
                    </span>
                  </button>
                )
              })}
            </Picker>
          )}
        </Field>

        <Field>
          <Hint label="covers" text="CADINU art only. Default: the banner of your avatar's level.">
            <h3>Cover</h3>
          </Hint>
          <Picker>
            {COVERS.map((c) => (
              <button key={c.id} type="button" aria-pressed={s?.cover === c.id} disabled={working} onClick={() => call('me', 'PATCH', { cover: c.id })} data-testid={`cover-${c.id}`}>
                <span style={{ width: '100%', pointerEvents: 'none' }}>
                  <Cover id={c.id} h={64} />
                </span>
                <span>{c.label}</span>
              </button>
            ))}
          </Picker>
        </Field>

        <Field>
          <Hint label="privacy" text="Public profile off: others see only your address, no handle, avatar, followers or profit. CADINU never shows an email address; your sign-in times are never public.">
            <h3>Privacy</h3>
          </Hint>
          <Toggle>
            <span>Public profile</span>
            <input type="checkbox" checked={Boolean(s?.visible)} disabled={working} onChange={(e) => call('me', 'PATCH', { visible: e.target.checked })} data-testid="toggle-visible" />
          </Toggle>
          {pnlOn && (
            <Toggle>
              <span>Show profit</span>
              <input type="checkbox" checked={Boolean(s?.show_pnl)} disabled={working} onChange={(e) => call('me', 'PATCH', { show_pnl: e.target.checked })} />
            </Toggle>
          )}
        </Field>

        {msg && <Muted role="alert">{msg}</Muted>}

        <div id="onchain-section" style={{ paddingTop: 16, borderTop: '1px solid rgba(255,255,255,0.06)', scrollMarginTop: 72 }}>
          <OnchainEditor wallet={me.address as `0x${string}`} onBuy={openBuy} />
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', paddingTop: 6, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <ActionBtn as="a" href={`/u/${me.handle ?? me.address}`}>
            <Icon name="external" size={18} />
            View public page
          </ActionBtn>
          {session && (
            <ActionBtn
              type="button"
              onClick={() => {
                signOut()
                setEdit(false)
              }}
              data-testid="sign-out"
            >
              <Icon name="logout" size={18} />
              Sign out
            </ActionBtn>
          )}
        </div>
      </Drawer>
      {buyDrawer}
    </Page>
  )
}
